import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {
  Modal,
  Title,
  Label,
  Input,
  SecondaryButton,
  PrimaryButton,
  IconButton,
  ConfirmMessage,
  ButtonGroup,
  ErrorMessage,
} from 'components';

import {Organization} from 'api/types/organisationTypes';
import {
  addOrganization,
  editOrganization,
  deleteOrganization,
  downloadResult,
} from './redux/thunks';
import {useDeleteButton} from 'hooks/useDeleteButton';
import i18n from 'i18next';

type AddOrganizationModalProps = {
  show: boolean;
  onClose: () => void;
  selectedOrganization?: Organization;
};

function AddOrganizationModal({
  show,
  onClose,
  selectedOrganization,
}: AddOrganizationModalProps) {
  const [organization, setOrganization] = useState<Organization>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const {showDeleteMessage, onShowDeleteMessage} = useDeleteButton(show);

  const dispatch = useDispatch();

  const isEditMode = !!selectedOrganization;
  const {
    id,
    name = '',
    maxContentCreators = 0,
    maxLearners = 0,
  } = organization || {};

  useEffect(() => {
    if (selectedOrganization) {
      setOrganization(selectedOrganization);
    } else {
      setOrganization(undefined);
    }
  }, [selectedOrganization]);

  const onUpdate =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setOrganization({
        ...(organization || {
          name: '',
          maxManagers: 0,
          maxLearners: 0,
          maxContentCreators: 0,
        }),
        [field]: event.target.value,
      });
    };

  const onSubmit = () => {
    if (!organization) {
      setErrorMessage(
        i18n.t(
          'manageOrganizationPage.addOrganizationModal.validationErrors.addOrganisation'
        )
      );
      return;
    }

    let message = undefined;
    const {name, maxContentCreators, maxLearners} = organization;

    if (!name) {
      message = i18n.t(
        'manageOrganizationPage.addOrganizationModal.validationErrors.organisationNameMissing'
      );
    } else if (maxContentCreators < 1 || maxLearners < 1) {
      message = i18n.t(
        'manageOrganizationPage.addOrganizationModal.validationErrors.invalidNumber'
      );
    }

    if (message) {
      setErrorMessage(message);
      return;
    }

    if (isEditMode) {
      dispatch(
        editOrganization({
          ...selectedOrganization,
          ...organization,
        })
      );
    } else {
      dispatch(addOrganization(organization));
    }

    setOrganization(undefined);

    onClose();
  };

  const onDelete = () => {
    if (selectedOrganization) {
      dispatch(deleteOrganization(selectedOrganization));
      onClose();
    }
  };

  const onDownload = () => {
    if (id) {
      dispatch(downloadResult(id));
    }
  };

  let header = i18n.t(
    'manageOrganizationPage.addOrganizationModal.newOrganisation'
  );
  let confirmText = i18n.t(
    'manageOrganizationPage.addOrganizationModal.addLabel'
  );

  if (isEditMode) {
    header = i18n.t(
      'manageOrganizationPage.addOrganizationModal.newOrganisationEditMode'
    );
    confirmText = i18n.t(
      'manageOrganizationPage.addOrganizationModal.addLabelEditMode'
    );
  }

  return (
    <Modal show={show} onClickOutside={onClose}>
      <div>
        <div className="flex justify-between mb-8">
          <div>
            <Title>{header}</Title>
          </div>
          {isEditMode && (
            <SecondaryButton className="px-4 py-3" onClick={onDownload}>
              Download
            </SecondaryButton>
          )}
        </div>
        <Label
          htmlFor="name"
          label={i18n.t(
            'manageOrganizationPage.addOrganizationModal.organisationNameLabel'
          )}
        >
          <Input
            id="name"
            value={name}
            onChange={onUpdate('name')}
            className="mb-4"
          />
        </Label>
        <Label
          htmlFor="max-content-creators"
          label={i18n.t(
            'manageOrganizationPage.addOrganizationModal.maxNumberOfContentCreators'
          )}
        >
          <Input
            id="max-content-creators"
            type="number"
            min="1"
            value={maxContentCreators}
            onChange={onUpdate('maxContentCreators')}
            className="mb-4"
          />
        </Label>
        <Label
          htmlFor="max-learners"
          label={i18n.t(
            'manageOrganizationPage.addOrganizationModal.maxNumberOfLearners'
          )}
        >
          <Input
            id="max-learners"
            type="number"
            min="1"
            value={maxLearners}
            onChange={onUpdate('maxLearners')}
          />
        </Label>
        {errorMessage && (
          <ErrorMessage className="my-4">{errorMessage}</ErrorMessage>
        )}
        <div className="flex mt-6 justify-between">
          <div>
            {isEditMode && (
              <ButtonGroup>
                <IconButton type="delete" onClick={onShowDeleteMessage} />
                {showDeleteMessage && (
                  <ConfirmMessage
                    className="h-36 w-72"
                    cancelText={i18n.t(
                      'manageOrganizationPage.deleteOrganisation.cancel'
                    )}
                    onCancel={onShowDeleteMessage}
                    confirmText={i18n.t(
                      'manageOrganizationPage.deleteOrganisation.confirm'
                    )}
                    onConfirm={onDelete}
                  >
                    {i18n.t('manageOrganizationPage.deleteOrganisation.prompt')}
                  </ConfirmMessage>
                )}
              </ButtonGroup>
            )}
          </div>
          <div>
            <SecondaryButton className="h-14 w-28" onClick={onClose}>
              {i18n.t('common.cancel')}
            </SecondaryButton>
            <PrimaryButton className="h-14 w-28 ml-4" onClick={onSubmit}>
              {confirmText}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AddOrganizationModal;
